import { useEffect, useState } from "react";
import "./css/ordertypelist.css";
import { apiGet, apiPost } from "../../utils/apiClient";
import { formatDateTime } from "../../utils/commonService";

const OrderTypeList = () => {
  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(todayStr);
  const [searchField, setSearchField] = useState("상품명");
  const [searchText, setSearchText] = useState("");
  const [orderType, setOrderType] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const handleReset = () => {
    setSearchField("상품명");
    setSearchText("");
    setStartDate("");
    setEndDate(new Date().toISOString().split("T")[0]);
    setOrderType("");
    setCurrentPage(0);
    loadOrders(0, "", "상품명", "", "", new Date().toISOString().split("T")[0]);
  };

  const loadOrders = async (page = 0, type = orderType, field = searchField, text = searchText, start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', pageSize);
      if (field && text) {
        params.append('searchType', field);
        params.append('searchTerm', text);
      }
      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);

      const url = type
        ? `/store/order/typelist/${type}?${params.toString()}`
        : `/store/order/typelist?${params.toString()}`;
      const response = await apiGet(url);
      const data = await response.json();
      
      if (response.ok) {
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        
        const orderList = (data.content || []).map(p => ({
          productNm: p.productsNm,
          buyer: p.buyer,
          totalPrice: p.price,
          payType: p.payment,
          payDate: p.paymentDateTime,
          saleType: p.orderType
        }));
        setList(orderList);
      } else {
        setList([]);
      }
    } catch (error) {
      console.error("주문 목록 조회 중 오류 발생:", error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleOrderTypeChange = (e) => {
    const value = e.target.value;
    setOrderType(value);
    setCurrentPage(0);
    loadOrders(0, value);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    loadOrders(0, orderType, searchField, searchText, startDate, endDate);
  };

  const excelDownload = async () => {
    try {
      const response = await apiPost(`/store/order/typelist/excel/${orderType || 'ALL'}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      const disposition = response.headers.get('Content-Disposition');
      const fileName = disposition
        ? decodeURIComponent(disposition.split('filename*=UTF-8\'\'')[1] || disposition.split('filename=')[1]?.replace(/"/g, ''))
        : '판매유형_리스트.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
    } catch (error) {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  }

  return (
    <div className="ordertypelist-wrap">
      <div className="ordertypelist-header">
        <div className="ordertypelist-header-title">판매유형</div>
        <div className="ordertypelist-header-excel" onClick={excelDownload}>엑셀 다운로드</div>
      </div>

      <div className="ordertypelist-filter">
        <div className="ordertypelist-date-range">
          <div className="ordertypelist-date-range-inner">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="ordertypelist-date-input"
            />
            <span className="ordertypelist-date-sep">~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="ordertypelist-date-input"
            />
          </div>

          <div className="ordertypelist-sale-type">
            <select
              value={orderType}
              onChange={handleOrderTypeChange}
              className="ordertypelist-search-select"
            >
              <option value="" disabled hidden>판매유형</option>
              <option value="ALL">전체</option>
              <option value="NORMAL">일반</option>
              <option value="DISCOUNT">할인</option>
              <option value="NEGO">네고</option>
            </select>
          </div>

          <div className="ordertypelist-search">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="ordertypelist-search-select"
            >
              <option>상품명</option>
              <option>구매자</option>
            </select>
            <input
              type="text"
              placeholder="검색어를 입력해주세요."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="ordertypelist-search-input"
            />
            <div className="ordertypelist-search-btn">
              <button className="ordertypelist-btn" onClick={handleSearch}>검색</button>
              <button className="ordertypelist-btn" onClick={handleReset}>
                초기화
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ordertypelist-count">
        전체 {totalElements}개 상품
      </div>

      <div className="product-table-wrap">
        <table className="product-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>상품명</th>
              <th>구매자</th>
              <th>판매유형</th>
              <th>결제금액</th>
              <th>결제</th>
              <th>결제일시</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>
                  로딩 중...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '20px'}}>
                  주문 내역이 없습니다.
                </td>
              </tr>
            ) : (
              list.map((p, idx) => (
                <tr key={p.orderCode || idx} className="product-table-row">
                  <td>{currentPage * pageSize + idx + 1}</td>
                  <td className="product-product-info">
                    {p.productNm}
                  </td>
                  <td>{p.buyer}</td>
                  <td>{p.saleType}</td>
                  <td>{p.totalPrice?.toLocaleString()}원</td>
                  <td>{p.payType}</td>
                  <td>{formatDateTime(p.payDate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="product-footer">
          <div className="product-pagination">
            <button 
              className="product-page" 
              onClick={() => {
                if (currentPage > 0) {
                  loadOrders(currentPage - 1);
                }
              }}
              disabled={currentPage === 0}
            >
              이전
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`product-page ${currentPage === i ? 'active' : ''}`}
                onClick={() => loadOrders(i)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className="product-page" 
              onClick={() => {
                if (currentPage < totalPages - 1) {
                  loadOrders(currentPage + 1);
                }
              }}
              disabled={currentPage === totalPages - 1}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTypeList;
